import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, INTERVAL_LABELS, TYPE_LABELS } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronRight, Plus, Pencil, Trash2, Loader2, Building2, Box } from "lucide-react";
import { toast } from "sonner";

export default function RaeumePage() {
  const [rooms, setRooms] = useState([]);
  const [objects, setObjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [taskDialog, setTaskDialog] = useState(null); // {objectId, task?}

  const load = async () => {
    const [r, o, t] = await Promise.all([api.get("/rooms"), api.get("/objects"), api.get("/tasks")]);
    setRooms(r.data); setObjects(o.data); setTasks(t.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addRoom = async () => {
    const name = prompt("Name des neuen Raums:");
    if (!name) return;
    await api.post("/rooms", { name }); toast.success("Raum angelegt"); load();
  };
  const renameRoom = async (room) => {
    const name = prompt("Raum umbenennen:", room.name);
    if (!name) return;
    await api.put(`/rooms/${room.id}`, { name }); load();
  };
  const delRoom = async (room) => {
    if (!window.confirm(`Raum "${room.name}" inkl. Objekte & Tätigkeiten löschen?`)) return;
    await api.delete(`/rooms/${room.id}`); toast.success("Raum gelöscht"); load();
  };
  const addObject = async (roomId) => {
    const name = prompt("Name des Objekts / Geräts:");
    if (!name) return;
    await api.post("/objects", { room_id: roomId, name }); load();
  };
  const renameObject = async (obj) => {
    const name = prompt("Objekt umbenennen:", obj.name);
    if (!name) return;
    await api.put(`/objects/${obj.id}`, { room_id: obj.room_id, name }); load();
  };
  const delObject = async (obj) => {
    if (!window.confirm(`Objekt "${obj.name}" löschen?`)) return;
    await api.delete(`/objects/${obj.id}`); load();
  };
  const delTask = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  if (loading) return <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>;

  return (
    <div>
      <PageHeader title="Räume & Objekte" subtitle="Vollständig konfigurierbar: Räume, Geräte, Messpunkte, Tätigkeiten & Grenzwerte">
        <Button data-testid="btn-add-room" onClick={addRoom}><Plus className="h-4 w-4 mr-1" /> Raum hinzufügen</Button>
      </PageHeader>

      <div className="space-y-2">
        {rooms.map((room) => {
          const roomObjs = objects.filter((o) => o.room_id === room.id);
          const isOpen = expanded[room.id];
          return (
            <div key={room.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="room-item">
              <div className="flex items-center gap-2 px-4 py-3">
                <button onClick={() => setExpanded({ ...expanded, [room.id]: !isOpen })} className="flex items-center gap-2 flex-1 text-left" data-testid="room-toggle">
                  <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <Building2 className="h-4 w-4 text-teal-500" />
                  <span className="font-semibold text-slate-800">{room.name}</span>
                  <span className="text-xs text-slate-400">({roomObjs.length} Objekte)</span>
                </button>
                <button onClick={() => renameRoom(room)} className="p-1.5 text-slate-400 hover:text-teal-600"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => delRoom(room)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid="btn-delete-room"><Trash2 className="h-4 w-4" /></button>
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 space-y-3">
                  {roomObjs.map((obj) => {
                    const objTasks = tasks.filter((t) => t.object_id === obj.id);
                    return (
                      <div key={obj.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid="object-item">
                        <div className="flex items-center gap-2">
                          <Box className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-700 text-sm flex-1">{obj.name}</span>
                          <button onClick={() => setTaskDialog({ objectId: obj.id })} className="text-xs font-medium text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="btn-add-task"><Plus className="h-3.5 w-3.5" /> Tätigkeit</button>
                          <button onClick={() => renameObject(obj)} className="p-1 text-slate-400 hover:text-teal-600"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => delObject(obj)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        {objTasks.length > 0 && (
                          <div className="mt-2 space-y-1 pl-6">
                            {objTasks.map((t) => (
                              <div key={t.id} className="flex items-center gap-2 text-sm" data-testid="task-item">
                                <span className="text-slate-700 flex-1">{t.name}</span>
                                <StatusBadge variant="neutral">{TYPE_LABELS[t.type] || t.type}</StatusBadge>
                                <StatusBadge variant="teal">{INTERVAL_LABELS[t.interval] || t.interval}</StatusBadge>
                                {t.temp_limit != null && <StatusBadge variant="due">max. {t.temp_limit}°C</StatusBadge>}
                                <button onClick={() => setTaskDialog({ objectId: obj.id, task: t })} className="p-1 text-slate-400 hover:text-teal-600"><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => delTask(t.id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button onClick={() => addObject(room.id)} className="text-sm font-medium text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="btn-add-object"><Plus className="h-4 w-4" /> Objekt / Gerät hinzufügen</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {taskDialog && (
        <TaskDialog data={taskDialog} onClose={() => setTaskDialog(null)} onDone={() => { setTaskDialog(null); load(); }} />
      )}
    </div>
  );
}

function TaskDialog({ data, onClose, onDone }) {
  const editing = !!data.task;
  const [form, setForm] = useState(data.task || { name: "", type: "cleaning", interval: "daily", temp_limit: "", method: "", active: true });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    const payload = {
      object_id: data.objectId, name: form.name, type: form.type, interval: form.interval,
      temp_limit: form.type === "temperature" && form.temp_limit !== "" ? parseFloat(form.temp_limit) : null,
      method: form.method || null, active: form.active !== false,
    };
    try {
      if (editing) await api.put(`/tasks/${data.task.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success("Gespeichert"); onDone();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="task-dialog">
        <DialogHeader><DialogTitle>{editing ? "Tätigkeit bearbeiten" : "Neue Tätigkeit"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Bezeichnung *</Label>
            <Input data-testid="input-task-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Art</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="select-task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cleaning">Reinigung / Desinfektion</SelectItem>
                  <SelectItem value="temperature">Temperatur</SelectItem>
                  <SelectItem value="generic">Prüfung</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Intervall</Label>
              <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                <SelectTrigger data-testid="select-task-interval"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">täglich</SelectItem>
                  <SelectItem value="operating_day">Betriebstag (ohne Di)</SelectItem>
                  <SelectItem value="weekly">wöchentlich</SelectItem>
                  <SelectItem value="monthly">monatlich</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          {form.type === "temperature" && (
            <div className="space-y-1.5"><Label>Grenzwert max. (°C)</Label>
              <Input type="number" step="0.1" data-testid="input-task-limit" value={form.temp_limit ?? ""} onChange={(e) => setForm({ ...form, temp_limit: e.target.value })} placeholder="z. B. 7 oder -18" /></div>
          )}
          {form.type !== "temperature" && (
            <div className="space-y-1.5"><Label>Womit / Wie (Methode)</Label>
              <Input data-testid="input-task-method" value={form.method || ""} onChange={(e) => setForm({ ...form, method: e.target.value })} placeholder="Mittel, Konzentration, Einwirkzeit" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button data-testid="btn-submit-task" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
